import * as React from 'react'

import { Box, Button, List, ProgressBar, Text } from '@factorialco/deprecated-design-system'

import PanesHeader from 'modules/core/components/PanesHeader'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import getPlanLabel from 'modules/billing/lib/getPlanLabel'
import { usePlanSettingsContext } from 'modules/billing/pages/settings/PlansSettingsContext'
import plans, { subscriptionPriceWithDiscount } from 'modules/billing/store/collections/plans'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import { SOLUTIONS } from 'modules/billing/store/models/Plan'
import { TIER } from 'modules/billing/store/models/Plan'
import InfoTooltip from 'modules/expenses/components/subscription/InfoTooltip'
import AddUsersModal from 'modules/expenses/pages/subscription/AddUsersModal'

const getIntervalsLabel = (type: string) => {
  switch (type) {
    case 'month':
      return i18n.t('billing.intervals.month')
    case 'year':
      return i18n.t('billing.intervals.year')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  expensesUserCount: number
  managedByPartnersUserLimit: number
  onCTAClick: () => void | Promise<void>
}

const YourPlan = ({ expensesUserCount, managedByPartnersUserLimit, onCTAClick }: Props) => {
  const factorialChargeBee = hasFeature('dev_chargebee')
  const subscription = subscriptions.current
  const plan = plans.current
  const planLabel = getPlanLabel(plan.type)
  const interval = subscription.scheduledInterval || plan.interval
  const intervalLabel = getIntervalsLabel(interval ?? '')
  const price = React.useRef<number>(subscriptionPriceWithDiscount(subscription)).current
  const customerPortal = subscription.chargebeeCustomerPortal
  const newCustomerPortal = buildUrl('/customer-portal?v2')
  const [addUsersModalOpen, setAddUsersModalOpen] = React.useState(false)
  const { featureAvailabilities } = usePlanSettingsContext()
  const partnerManaged = hasFeature('managed_by_partner')
  const { navigation } = useNavigation()

  const openCustomerPortal = () => {
    if (factorialChargeBee) {
      window?.open(customerPortal, '_blank')?.focus()
    }
  }

  const solutionTranslation = () => {
    if (plan.solutions === SOLUTIONS.coreTalent) {
      return i18n.t('billing.bill.solutions.core_and_talent')
    } else if (plan.solutions === SOLUTIONS.coreTime) {
      return i18n.t('billing.bill.solutions.core_and_time')
    } else if (plan.solutions === SOLUTIONS.completeBundle) {
      return i18n.t('billing.bill.solutions.complete_bundle')
    } else {
      return ''
    }
  }

  const tierTranslation = () => {
    if (plan.tier === TIER.business) {
      return i18n.t('billing.bill.tier.business')
    } else if (plan.tier === TIER.enterprise) {
      return i18n.t('billing.bill.tier.enterprise')
    } else if (plan.tier === TIER.certification) {
      return i18n.t('billing.bill.tier.certification')
    } else {
      return ''
    }
  }

  if (plan.isLegacy && !factorialChargeBee) return null

  return (
    <>
      {factorialChargeBee && (
        <Box gap="s24">
          {featureAvailabilities.expensesPerUserPricing && (
            <Box flexDirection="row">
              <Box flexDirection="row" flexGrow>
                <PanesHeader
                  illustration="user-male-female"
                  title={i18n.t('billing.subscription.manage_users.title')}
                  description={i18n.t('billing.subscription.manage_users.description')}
                />
              </Box>

              <Box justifyContent="flexEnd" flexDirection="row" gap="s12" flexGrow>
                <List>
                  <List.Item>
                    <Box gap="s12" minWidth="s400">
                      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
                        <Box>
                          <Text size="300" weight="semibold">
                            {i18n.t('billing.subscription.manage_users.expenses_users')}
                          </Text>
                          {partnerManaged && managedByPartnersUserLimit > 0 ? (
                            <Box flexDirection="row" alignItems="center" gap="s4" paddingTop="s4">
                              <Box width="s200">
                                <ProgressBar
                                  amount={expensesUserCount}
                                  total={managedByPartnersUserLimit}
                                  showLabel
                                />
                              </Box>
                              <InfoTooltip
                                label={i18n.t(
                                  'billing.subscription.plans.expenses.managed_by_partners.tooltip',
                                  {
                                    current: expensesUserCount,
                                    limit: managedByPartnersUserLimit,
                                  }
                                )}
                              />
                            </Box>
                          ) : (
                            <Text size="200" weight="medium">
                              {i18n.tp('billing.subscription.manage_users.expenses_users_count', {
                                count: expensesUserCount,
                              })}
                            </Text>
                          )}
                        </Box>
                        <Button.Router
                          to={navigation.settings.plans.expenses.fullPath()}
                          type="secondary"
                          iconName="arrow-right"
                        />
                      </Box>
                      <Button
                        onClick={() => setAddUsersModalOpen(true)}
                        iconName="add"
                        disabled={
                          partnerManaged &&
                          (!(managedByPartnersUserLimit > 0) ||
                            expensesUserCount >= managedByPartnersUserLimit)
                        }
                      >
                        {i18n.t('billing.subscription.plans.expenses.actions.add_users')}
                      </Button>
                    </Box>
                  </List.Item>
                </List>
              </Box>
              {addUsersModalOpen && <AddUsersModal onClose={() => setAddUsersModalOpen(false)} />}
            </Box>
          )}
        </Box>
      )}
    </>
  )
}

export default YourPlan
